/**
 * React Query hooks for NaraShape recipe versioning.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getShapeRecipeDraft,
  getShapeRecipeVersions,
  getShapeRecipeVersion,
  publishShapeRecipe,
  activateShapeRecipeVersion,
  type ShapeRecipe,
  type ShapeRecipeVersionsResponse,
  type ShapeRecipeVersion,
} from "../services/naraDoor";

export function useShapeRecipeDraft(recipeId: string | undefined) {
  return useQuery<ShapeRecipe>({
    queryKey: ["shapeRecipeDraft", recipeId],
    queryFn: () => {
      if (!recipeId) throw new Error("recipeId is required");
      return getShapeRecipeDraft(recipeId);
    },
    enabled: !!recipeId,
  });
}

export function useShapeRecipeVersions(recipeId: string | undefined, enabled = true) {
  return useQuery<ShapeRecipeVersionsResponse>({
    queryKey: ["shapeRecipeVersions", recipeId],
    queryFn: () => {
      if (!recipeId) throw new Error("recipeId is required");
      return getShapeRecipeVersions(recipeId).catch((err) => {
        console.error("Failed to load recipe versions:", err);
        throw err;
      });
    },
    enabled: !!recipeId && enabled,
  });
}

export function useShapeRecipeVersion(recipeId: string | undefined, version: number | null) {
  return useQuery<ShapeRecipeVersion>({
    queryKey: ["shapeRecipeVersion", recipeId, version],
    queryFn: () => {
      if (!recipeId || version === null) throw new Error("recipeId and version are required");
      return getShapeRecipeVersion(recipeId, version);
    },
    enabled: !!recipeId && version !== null,
  });
}

export function usePublishShapeRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ recipeId }: { recipeId: string }) => {
      return publishShapeRecipe(recipeId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["shapeRecipeDraft", variables.recipeId] });
      queryClient.invalidateQueries({ queryKey: ["shapeRecipeVersions", variables.recipeId] });
      queryClient.invalidateQueries({ queryKey: ["shapeRecipe", variables.recipeId] });
      queryClient.invalidateQueries({ queryKey: ["shapeRecipes"] });
    },
  });
}

export function useActivateShapeRecipeVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ recipeId, version }: { recipeId: string; version: number }) => {
      return activateShapeRecipeVersion(recipeId, version);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["shapeRecipeDraft", variables.recipeId] });
      queryClient.invalidateQueries({ queryKey: ["shapeRecipeVersions", variables.recipeId] });
      queryClient.invalidateQueries({ queryKey: ["shapeRecipe", variables.recipeId] });
      queryClient.invalidateQueries({ queryKey: ["shapeRecipes"] });
    },
  });
}
